import React, { useEffect, useState } from 'react';

interface SettingsProps {
  storageStatus?: {
    storage: 'mongodb' | 'file' | 'unknown' | 'loading';
    message: string;
    error: string;
  };
}

const Settings: React.FC<SettingsProps> = ({ storageStatus }) => {
  const [shareMessage, setShareMessage] = useState('');
  const [saved, setSaved] = useState(false);
  const [exportedSettings, setExportedSettings] = useState<string>('');

  useEffect(() => {
    const existing = localStorage.getItem('share_message_template') || '';
    const legacyTemplate = 'Check out {title} by Carmen Lorraine Photography!';
    const defaultTemplate = '{description}\n\nCategory: {category}\n\n{hashtags}';
    if (!existing || existing.trim().startsWith(legacyTemplate)) {
      localStorage.setItem('share_message_template', defaultTemplate);
      setShareMessage(defaultTemplate);
      return;
    }
    setShareMessage(existing);
  }, []);

  const saveSettings = () => {
    localStorage.setItem('share_message_template', shareMessage.trim());
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const exportAllSettings = () => {
    const settings: Record<string, any> = {};
    
    // List of all localStorage keys used in the application
    const keys = [
      'website_content',
      'website_logo',
      'display_settings',
      'seo_settings',
      'admin-theme',
      'package_customization',
      'service_customization',
      'currency-settings',
      'admin_categories',
      'admin_galleries',
      'admin_blog_posts',
      'admin_blog_categories',
      'admin_addons',
      'share_message_template'
    ];
    
    keys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        try {
          settings[key] = JSON.parse(value);
        } catch (e) {
          settings[key] = value; // Store as string if not JSON
        }
      }
    });
    
    // Also get any other keys that might exist
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && !keys.includes(key) && (key.startsWith('admin_') || key.includes('settings') || key.includes('theme'))) {
        try {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              settings[key] = JSON.parse(value);
            } catch (e) {
              settings[key] = value;
            }
          }
        } catch (e) {
          // Skip
        }
      }
    }
    
    const jsonString = JSON.stringify(settings, null, 2);
    setExportedSettings(jsonString);
    
    // Copy to clipboard
    navigator.clipboard.writeText(jsonString).then(() => {
      alert('✅ All settings exported and copied to clipboard!');
    }).catch(() => {
      alert('Settings exported (but could not copy to clipboard - please copy manually)');
    });
  };

  return (
    <section className="admin-section">
      <div className="section-header">
        <h2>Share message</h2>
        <p>The wording used when a visitor shares a gallery or a post.</p>
      </div>
      <div className="section-content">
        {storageStatus && (
          <div className={`admin-storage-panel storage-${storageStatus.storage}`}>
            <h3>Storage</h3>
            <p>
              <strong>{storageStatus.storage === 'loading' ? 'Checking' : storageStatus.storage}</strong>
              {' '}{storageStatus.message}
            </p>
            {storageStatus.error && <p className="storage-error">{storageStatus.error}</p>}
          </div>
        )}
        <div style={{ display: 'grid', gap: '12px', maxWidth: 700 }}>
          <label htmlFor="shareMessage" style={{ fontWeight: 600 }}>Default Share Message (optional)</label>
          <textarea
            id="shareMessage"
            value={shareMessage}
            onChange={(e) => setShareMessage(e.target.value)}
            placeholder="{description}\n\nCategory: {category}\n\n{hashtags}"
            rows={5}
            style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #ccc' }}
          />
          <button onClick={saveSettings} style={{ width: 'fit-content', padding: '10px 16px', borderRadius: 8, border: 'none', background: '#222', color: '#fff', cursor: 'pointer' }}>Save</button>
          {saved && <span style={{ color: 'green' }}>Saved</span>}
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: '10px' }}>
            <p><strong>Available Variables:</strong></p>
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              <li><code>{'{title}'}</code> - Gallery/blog title</li>
              <li><code>{'{category}'}</code> - Content category</li>
              <li><code>{'{url}'}</code> - Shareable URL</li>
              <li><code>{'{description}'}</code> - Content description</li>
              <li><code>{'{hashtags}'}</code> - Formatted hashtags</li>
            </ul>
            
            <p><strong>Example Template:</strong></p>
            <div style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '11px' }}>
              {`{description}`}<br/><br/>
              Category: {`{category}`}<br/><br/>
              {`{hashtags}`}
            </div>
            
            <p><strong>Platform Support:</strong></p>
            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
              <li><strong>X (formerly Twitter) & Telegram:</strong> Use your custom message template</li>
              <li><strong>Facebook, LinkedIn, Pinterest:</strong> Use Open Graph meta tags (template ignored)</li>
              <li><strong>WhatsApp:</strong> Uses URL only for image preview</li>
              <li><strong>Copy Link:</strong> Includes your template text</li>
            </ul>
          </div>
        </div>

        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e0e0e0' }}>
          <h3 style={{ marginBottom: '1rem' }}>Export Settings as Defaults</h3>
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Export all your current settings to use as default values in the code. This is useful when you want to preserve your localhost configuration.
          </p>
          <button 
            onClick={exportAllSettings}
            style={{ 
              padding: '12px 24px', 
              borderRadius: 8, 
              border: 'none', 
              background: '#8B4513', 
              color: '#fff', 
              cursor: 'pointer',
              fontWeight: 600,
              marginBottom: '1rem'
            }}
          >
            <i className="fas fa-download" style={{ marginRight: '8px' }}></i>
            Export All Settings
          </button>
          
          {exportedSettings && (
            <div style={{ marginTop: '1rem' }}>
              <label style={{ fontWeight: 600, display: 'block', marginBottom: '0.5rem' }}>
                Exported Settings (copied to clipboard):
              </label>
              <textarea
                value={exportedSettings}
                readOnly
                rows={20}
                style={{ 
                  width: '100%', 
                  padding: '12px', 
                  borderRadius: 8, 
                  border: '1px solid #ccc',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  backgroundColor: '#f9f9f9'
                }}
              />
              <p style={{ marginTop: '0.5rem', fontSize: '12px', color: '#666' }}>
                Copy this JSON and provide it to update the default values in <code>src/config/defaults.ts</code>
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Settings;



